import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Actividad,
  CheckItem, crearCheckList,
  createEmptyCheckListFormInput, CurrentEmpresa
} from '@rpe/registro-personas-empresas-lib';
import { Observable, Subscription } from 'rxjs';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { ROUTE_ANIMATIONS_ELEMENTS } from '../../../../core/core.module';
import { selectCurrentEmpresa } from '../../../empresas/entity-mis-empresas/entity-current-empresa.selectors';
import { selectActividadFromCurrentEmpresaByTipoActividad } from '../../entity-actividades/entity-actividades.selectors';
import { State } from '../../../vista-usuario/vista-usuario.state';
import { entityComercializacionesAddRequest } from '../../entity-comercializaciones/comercializaciones.actions';
import {
  Comercializacion,
  createComercializacion
} from '../../entity-comercializaciones/comercializaciones.model';
import { selectAllComercializacionesFromCurrentEmpresa } from '../../entity-comercializaciones/comercializaciones.selectors';
import {
  selectAllGruposEspecie,
  selectGruposEspeciesByTipoActividad
} from '../../entity-grupos-especie/grupos-especie.selectors';

@Component({
  selector: 'rpe-registro-actividad-comercializacion-form',
  templateUrl: './registro-actividad-comercializacion-form.component.html',
  styleUrls: ['./registro-actividad-comercializacion-form.component.scss']
})
export class RegistroActividadComercializacionFormComponent implements OnInit, OnDestroy {
  @Input() formInput: any = null;
  
  //se utilizan para que el usuario elija si o no en la comercializacion
  comSemProducida: boolean;
  comSemImportada: boolean;
  reventaSemilla: boolean;

  //se usan para validar si tiene registradas las actividades
  currentEmpresa$: Observable<CurrentEmpresa>;
  actividadProduccion$: Observable<Actividad>;
  actividadImportacion$: Observable<Actividad>;
  actividadComercializacion$: Observable<Actividad>;
  hayImportacionSemilla = false;

  registroActividadComercializacionFormGroup: FormGroup;
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;

  formInputSemProd = createEmptyCheckListFormInput();
  semillaProducidaValid = false;
  semillaProducida: Comercializacion[] = [];
  gpProducida$: Observable<CheckItem[]>;

  formInputSemImp = createEmptyCheckListFormInput();
  semillaImportadaValid = false;
  semillaImportada: Comercializacion[] = [];
  gpImportada$: Observable<CheckItem[]>;

  formInputSemRev = createEmptyCheckListFormInput();
  semillaReventaValid = false;
  semillaReventa: Comercializacion[] = [];  
  gpReventa$: Observable<CheckItem[]>;

  subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private store: Store<State>
  ) {}
  
  ngOnInit(): void {    
    this.registroActividadComercializacionFormGroup = this.fb.group({
    });

    this.currentEmpresa$ = this.store.select(selectCurrentEmpresa);

    // Cargo los gp
    this.gpProducida$ = combineLatest([
      this.store.select(selectGruposEspeciesByTipoActividad(9)),
      this.store.select(selectAllComercializacionesFromCurrentEmpresa)])
    .pipe(
      map(([gruposEspecies, comercializaciones]) => {
        let grupoEspecieListAux: CheckItem[] = [];
        if (comercializaciones) {          
          gruposEspecies.forEach(gp => {
            const comercializacion = comercializaciones.find(c => c.tipo_comercializacion_id == 1 && c.grupo_especie_id == gp.id);
            grupoEspecieListAux.push({
              id: comercializacion ? comercializacion.id : null,
              tipo_id: gp.id,
              nombre: gp.nombre,
              selected: comercializacion ? true : false
            });
          });                  
        } else {
          grupoEspecieListAux = crearCheckList(gruposEspecies).elementosCheck
        }
        return grupoEspecieListAux          
      })
    );
    const s1 = this.gpProducida$.subscribe(gp => {
      this.formInputSemProd.elementosCheck = gp;
      this.checkRadio(1, gp);
    });

    this.gpImportada$ = combineLatest([
      this.store.select(selectGruposEspeciesByTipoActividad(6)),
      this.store.select(selectAllComercializacionesFromCurrentEmpresa)])
    .pipe(
      map(([gruposEspecies, comercializaciones]) => {
        let grupoEspecieListAux: CheckItem[] = [];
        if (comercializaciones) {          
          gruposEspecies.forEach(gp => {
            const comercializacion = comercializaciones.find(c => c.tipo_comercializacion_id == 2 && c.grupo_especie_id == gp.id);
            grupoEspecieListAux.push({
              id: comercializacion ? comercializacion.id : null,
              tipo_id: gp.id,
              nombre: gp.nombre,
              selected: comercializacion ? true : false
            });
          });                  
        } else {
          grupoEspecieListAux = crearCheckList(gruposEspecies).elementosCheck
        }
        return grupoEspecieListAux          
      })
    );
    const s2 = this.gpImportada$.subscribe(gp => {
      this.formInputSemImp.elementosCheck = gp;
      this.checkRadio(2, gp);
    });

    this.gpReventa$ = combineLatest([
      this.store.select(selectAllGruposEspecie),
      this.store.select(selectAllComercializacionesFromCurrentEmpresa)])
    .pipe(
      map(([gruposEspecies, comercializaciones]) => {
        let grupoEspecieListAux: CheckItem[] = [];
        if (comercializaciones) {          
          gruposEspecies.forEach(gp => {
            const comercializacion = comercializaciones.find(c => c.tipo_comercializacion_id == 3 && c.grupo_especie_id == gp.id);
            grupoEspecieListAux.push({
              id: comercializacion ? comercializacion.id : null,
              tipo_id: gp.id,
              nombre: gp.nombre,
              selected: comercializacion ? true : false
            });
          });                  
        } else {
          grupoEspecieListAux = crearCheckList(gruposEspecies).elementosCheck
        }
        return grupoEspecieListAux          
      })
    );
    const s3 = this.gpReventa$.subscribe(gp => {
      this.formInputSemRev.elementosCheck = gp;
      this.checkRadio(3, gp);
    }
    );

    this.subscriptions.push(s1, s2, s3);

    // Existe actividad de produccion
    this.actividadProduccion$ = this.store.select(
      selectActividadFromCurrentEmpresaByTipoActividad(9)
    );
    this.actividadImportacion$ = this.store.select(
      selectActividadFromCurrentEmpresaByTipoActividad(6)
    );
    this.actividadComercializacion$ = this.store.select(
      selectActividadFromCurrentEmpresaByTipoActividad(2)
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  reset(): void {}

  registrarActividad() {
    this.router.navigate(['../vista-registro-actividad']);
  }

  onShowErrors(): void {
    this.registroActividadComercializacionFormGroup.markAllAsTouched();
  }

  checkRadio(tc: number, cil: CheckItem[]){
    switch (tc){
      case 1:
        this.comSemProducida = cil.find(ci => ci.selected == true) ? true : false;
        break;
      case 2:
        this.comSemImportada = cil.find(ci => ci.selected == true) ? true : false;
        break;
      case 3:
        this.reventaSemilla = cil.find(ci => ci.selected == true) ? true : false;
        break;
      default:
        break;
    }
  }

  isValid(): boolean {
    // es valido si selecciona todos los radios y al menos selecciona un valor    
    const radioCheck = this.comSemProducida != undefined && this.comSemImportada != undefined && this.reventaSemilla != undefined
    return radioCheck && 
      (this.comSemProducida && this.semillaProducidaValid || 
        this.comSemImportada && this.semillaImportadaValid || 
        this.reventaSemilla && this.semillaReventaValid)
  }

  // Semilla producida
  semProdStatusChange(s: string) {
    this.semillaProducidaValid = s == 'VALID';
  }

  semProdChange(gp: CheckItem[]) {
    this.semillaProducida = [];
    gp.forEach((ci) => {
      this.semillaProducida.push(
        createComercializacion({
          id: ci.id,
          actividad_id: this.formInput.actividadId,
          tipo_comercializacion_id: 1, // Producida
          grupo_especie_id: ci.tipo_id
        })
      );
    });
  }

  // Semilla importada
  semImpStatusChange(s: string) {
    this.semillaImportadaValid = s == 'VALID';
  }

  semImpChange(gp: CheckItem[]) {
    this.semillaImportada = [];
    gp.forEach((ci) => {
      this.semillaImportada.push(
        createComercializacion({
          id: ci.id,
          actividad_id: this.formInput.actividadId,
          tipo_comercializacion_id: 2, // Importada
          grupo_especie_id: ci.tipo_id
        })
      );
    });
  }

  // Semilla reventa
  semRevStatusChange(s: string) {
    this.semillaReventaValid = s == 'VALID';
  }

  semRevChange(gp: CheckItem[]) {
    this.semillaReventa = [];
    gp.forEach((ci) => {
      this.semillaReventa.push(
        createComercializacion({
          id: ci.id,
          actividad_id: this.formInput.actividadId,
          tipo_comercializacion_id: 3, // Reventa
          grupo_especie_id: ci.tipo_id
        })
      );
    });
  }

  onGuardar() {
    if (this.isValid()){
      const comercializaciones = [
        ...this.semillaImportada ,
        ...this.semillaProducida,
        ...this.semillaReventa
      ];
      this.store.dispatch(
        entityComercializacionesAddRequest({item: comercializaciones})
      )
    } else {
      this.onShowErrors()
    }
  }

  onCancel(){}
}
